// menubarChipState — what the character chip at the left of the menubar says.
//
// The chip is the one control that must read correctly in every boot state,
// including the ones where nothing else on screen works yet. That makes its copy
// worth pinning in a pure function rather than deriving it inline in JSX: the
// states below are the actual test of the consolidation ("flip BOOT STATE
// to see the real test" in the approved design), and a unit test can flip them
// without a browser.
//
// The four states, and what each has to communicate:
//
//   no character  → the chip is still a live control. It says so plainly rather
//                   than rendering an empty box that looks broken.
//   loading       → WHICH character, and how far along. The byte readout comes
//                   from assetLoadProgress, which is why this takes bytes rather
//                   than a percentage: "4.2 / 11.8 MB" tells you a stalled
//                   download is stalled; "37%" does not.
//   loaded        → id and pipeline, because the name alone doesn't say whether
//                   you're looking at a rig or a splat.
//   switching     → WHICH character you just picked, immediately. commitSwitch
//                   resolves the target over the network (clearCharactersCache +
//                   loadCharacters + applyCharacterPack) before it touches
//                   `loaded`, so every other state here was still describing the
//                   OUTGOING character while the user waited — measured at 150s
//                   on a congested box, and a few hundred ms at best, which is
//                   still past the 400ms Doherty threshold with nothing on screen
//                   acknowledging the click.
//
// No imports — leaf module, driven directly by tests/unit/menubarChipState.test.mjs.

/** Pipeline label per characterRigStatus classification. Mirrors the launcher's
 *  STATUS map (src/components/PipelineGlyph.jsx) so one character reads the same
 *  in the picker and in the chip. */
const STATUS_LABEL = {
  rig: 'RIG',
  splat: 'GSPLAT',
  stream: 'STREAM',
  missing: 'NO RIG',
}

const BYTES_PER_MB = 1024 * 1024

/**
 * "4.2 / 11.8 MB", or null when the totals aren't known yet.
 *
 * Returns null rather than "0.0 / 0.0 MB" for a zero total: a download with no
 * Content-Length is a real state (assetLoadProgress flags it `indeterminate`),
 * and inventing a denominator would claim progress information we don't have.
 */
export function formatBytesProgress(receivedBytes, totalBytes) {
  if (!Number.isFinite(totalBytes) || totalBytes <= 0) return null
  const received = Number.isFinite(receivedBytes) ? Math.max(0, Math.min(receivedBytes, totalBytes)) : 0
  return `${(received / BYTES_PER_MB).toFixed(1)} / ${(totalBytes / BYTES_PER_MB).toFixed(1)} MB`
}

/**
 * @param {object}  input
 * @param {{id?: string, label?: string, name?: string}|null} [input.character]
 *   the active character record, or null before one is chosen.
 * @param {{id?: string, label?: string}|null} [input.switchingTo]
 *   the character a switch is resolving TOWARD, before it has committed.
 * @param {boolean} [input.loading]   true while the rig/bundle is still loading.
 * @param {{receivedBytes?: number, totalBytes?: number}|null} [input.progress]
 *   an assetLoadProgress snapshot.
 * @param {string|null} [input.stage] AvatarModel's current stage label — the
 *   fallback readout when byte totals are unknown.
 * @param {'rig'|'splat'|'stream'|'missing'|'unknown'|null} [input.rigStatus]
 * @param {'clean'|'dirty'|'saving'|'error'|'stale-backend'} [input.dirtyState]
 * @returns {{name: string, sub: string, spinning: boolean, muted: boolean,
 *            dirtyState: string, empty: boolean, switchingId: string|null}}
 */
export function deriveChipState({
  character = null,
  switchingTo = null,
  loading = false,
  progress = null,
  stage = null,
  rigStatus = null,
  dirtyState = 'clean',
} = {}) {
  // FIRST, ahead of every other branch — including the no-character one, so
  // picking the very first character doesn't read "No character" for the whole
  // resolve, and ahead of `loading`, because a switch picked mid-load makes those
  // bytes a progress bar for work being abandoned.
  //
  // Needs a label or an id: a switch we cannot name is one we cannot describe, so
  // it falls through to the ordinary states rather than rendering a blank pending
  // chip. Nothing here invents a name.
  const switchingName = switchingTo?.label ?? switchingTo?.id ?? null
  if (switchingName) {
    return {
      name: switchingName,
      sub: 'switching…',
      spinning: true,
      muted: false,
      // The badge sits beside the name, and the name is now the INCOMING
      // character — leaving it on would point the outgoing character's unsaved
      // edits at the wrong label. Same reasoning as the no-character branch.
      dirtyState: 'clean',
      empty: false,
      // The view crossfades when the target changes and needs a stable key.
      switchingId: switchingTo?.id ?? null,
    }
  }

  if (!character) {
    return {
      name: 'No character',
      sub: 'select a character',
      spinning: false,
      muted: true,
      // A character you don't have cannot have unsaved edits. Suppress rather
      // than trust a caller's stale flag — the badge would point at nothing.
      dirtyState: 'clean',
      empty: true,
      switchingId: null,
    }
  }

  const name = character.label ?? character.name ?? character.id ?? 'Character'

  if (loading) {
    const bytes = formatBytesProgress(progress?.receivedBytes, progress?.totalBytes)
    return {
      name,
      // Named for what it is, in the design's own words. With no byte totals we
      // fall back to AvatarModel's stage label, and only then to a bare
      // "loading…" — each rung says strictly less, and none of them invents a
      // number.
      sub: bytes ? `loading rig · ${bytes}` : (stage || 'loading…'),
      spinning: true,
      muted: false,
      dirtyState,
      empty: false,
      switchingId: null,
    }
  }

  const id = character.id ?? ''
  const label = rigStatus && rigStatus !== 'unknown' ? STATUS_LABEL[rigStatus] : null
  return {
    name,
    // An unprobed character shows its id alone rather than guessing a pipeline —
    // same rule the launcher cards follow ('unknown' renders no badge).
    sub: label ? `${id} · ${label}` : id,
    spinning: false,
    muted: false,
    dirtyState,
    empty: false,
    switchingId: null,
  }
}

export { STATUS_LABEL }
